/**
 * SweatDecks — Custom Article JavaScript
 * =======================================
 * Handles:
 *  1. Reading time calculation
 *  2. Table of Contents generation (from H2 headings)
 *  3. TOC active state tracking (Intersection Observer)
 *  4. TOC mobile collapse toggle
 *  5. FAQ accordion with smooth animation
 *  6. FAQ schema (JSON-LD) injection
 *  7. TL;DR section auto-detection and styling
 *  8. Table wrapping for mobile responsiveness
 *  9. Sources section styling
 * 10. Reading progress bar
 * 11. Back to top button
 * 12. Smooth scroll for anchor links
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     UTILITIES
     ----------------------------------------------------------------------- */

  /**
   * Slugify a string for use as an HTML id.
   * @param {string} text
   * @returns {string}
   */
  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80);
  }

  /**
   * Estimate reading time from text content.
   * Average adult reads ~238 wpm; we round to nearest minute, minimum 1 min.
   * @param {string} text
   * @returns {number} minutes
   */
  function calculateReadingTime(text) {
    var words = text.trim().split(/\s+/).length;
    return Math.max(1, Math.round(words / 238));
  }

  /**
   * Debounce helper.
   * @param {Function} fn
   * @param {number} delay
   * @returns {Function}
   */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  /* -----------------------------------------------------------------------
     DOM REFERENCES
     ----------------------------------------------------------------------- */
  var articleContent = document.querySelector('[data-article-content]');
  var tocSidebar = document.querySelector('[data-toc-sidebar]');
  var tocList = document.querySelector('[data-toc-list]');
  var tocToggle = document.querySelector('[data-toc-toggle]');
  var readingTimeEl = document.querySelector('[data-reading-time]');
  var progressBar = document.querySelector('[data-progress-bar]');
  var progressFill = document.querySelector('[data-progress-fill]');
  var backToTopBtn = document.querySelector('[data-back-to-top]');

  if (!articleContent) return;

  /* -----------------------------------------------------------------------
     1. READING TIME
     ----------------------------------------------------------------------- */
  if (readingTimeEl) {
    var bodyText = articleContent.textContent || articleContent.innerText || '';
    var minutes = calculateReadingTime(bodyText);
    readingTimeEl.textContent = minutes + ' min read';
  }

  /* -----------------------------------------------------------------------
     2. TABLE OF CONTENTS GENERATION
     ----------------------------------------------------------------------- */
  var headings = articleContent.querySelectorAll('h2');
  var tocHeadings = []; // Store references for scroll tracking

  if (tocList && headings.length > 1) {
    // Build title
    var tocTitle = document.createElement('p');
    tocTitle.className = 'sd-toc__title';
    tocTitle.textContent = 'Table of Contents';
    tocList.appendChild(tocTitle);

    // Build list
    var ul = document.createElement('ul');
    ul.className = 'sd-toc__list';

    headings.forEach(function (heading, index) {
      // Skip headings that are part of sources/related sections (optional)
      var headingText = (heading.textContent || '').trim();

      // Generate an id if missing
      if (!heading.id) {
        heading.id = slugify(headingText) || 'section-' + index;
      }

      // Ensure unique IDs
      var baseId = heading.id;
      var counter = 1;
      while (document.querySelectorAll('#' + CSS.escape(heading.id)).length > 1) {
        heading.id = baseId + '-' + counter;
        counter++;
      }

      tocHeadings.push(heading);

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'sd-toc__link';
      a.href = '#' + heading.id;
      a.textContent = headingText;
      a.setAttribute('data-toc-index', index);

      li.appendChild(a);
      ul.appendChild(li);
    });

    tocList.appendChild(ul);
  } else if (tocSidebar) {
    // Hide TOC if fewer than 2 headings
    tocSidebar.style.display = 'none';
    // Make content full width
    var layout = document.querySelector('.sd-article__layout');
    if (layout) {
      layout.style.gridTemplateColumns = '1fr';
      layout.style.maxWidth = 'var(--sd-content-width)';
    }
  }

  /* -----------------------------------------------------------------------
     3. TOC ACTIVE STATE TRACKING (Intersection Observer)
     ----------------------------------------------------------------------- */
  if (tocHeadings.length > 0) {
    var tocLinks = tocList ? tocList.querySelectorAll('.sd-toc__link') : [];

    // Track which headings are in view
    var activeHeadingIndex = 0;

    var observerOptions = {
      root: null,
      rootMargin: '-80px 0px -70% 0px',
      threshold: 0
    };

    var headingObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = tocHeadings.indexOf(entry.target);
          if (idx !== -1) {
            activeHeadingIndex = idx;
            updateActiveTocLink(idx);
          }
        }
      });
    }, observerOptions);

    tocHeadings.forEach(function (heading) {
      headingObserver.observe(heading);
    });

    function updateActiveTocLink(index) {
      tocLinks.forEach(function (link) {
        link.classList.remove('is-active');
      });
      if (tocLinks[index]) {
        tocLinks[index].classList.add('is-active');
      }
    }

    // Set first as active initially
    if (tocLinks.length > 0) {
      tocLinks[0].classList.add('is-active');
    }
  }

  /* -----------------------------------------------------------------------
     4. TOC MOBILE COLLAPSE TOGGLE
     ----------------------------------------------------------------------- */
  if (tocToggle && tocList) {
    tocToggle.addEventListener('click', function () {
      var expanded = tocToggle.getAttribute('aria-expanded') === 'true';
      tocToggle.setAttribute('aria-expanded', String(!expanded));
      tocList.classList.toggle('is-open');
    });

    // Close TOC when a link is clicked (mobile)
    if (tocList) {
      tocList.addEventListener('click', function (e) {
        if (e.target.matches('.sd-toc__link')) {
          // Only close on mobile
          if (window.innerWidth <= 1024) {
            tocToggle.setAttribute('aria-expanded', 'false');
            tocList.classList.remove('is-open');
          }
        }
      });
    }
  }

  /* -----------------------------------------------------------------------
     5. FAQ ACCORDION
     ----------------------------------------------------------------------- */
  // Detect FAQ section: look for an H2 with "FAQ" or "Frequently Asked" in text
  var faqSectionHeading = null;
  headings.forEach(function (h) {
    var txt = (h.textContent || '').toLowerCase();
    if (txt.indexOf('faq') !== -1 || txt.indexOf('frequently asked') !== -1) {
      faqSectionHeading = h;
    }
  });

  var faqData = []; // For schema generation

  if (faqSectionHeading) {
    // Collect all H3 siblings between this H2 and the next H2
    var faqContainer = document.createElement('div');
    faqContainer.className = 'sd-faq-section';
    faqSectionHeading.parentNode.insertBefore(faqContainer, faqSectionHeading.nextSibling);

    // Gather elements between FAQ H2 and next H2 (or end)
    var faqElements = [];
    var sibling = faqContainer.nextSibling;
    while (sibling) {
      if (sibling.nodeType === 1 && sibling.tagName === 'H2') break;
      faqElements.push(sibling);
      sibling = sibling.nextSibling;
    }

    // Move elements into faqContainer
    faqElements.forEach(function (el) {
      faqContainer.appendChild(el);
    });

    // Now process H3s inside faqContainer as accordion items
    var faqH3s = faqContainer.querySelectorAll('h3');

    faqH3s.forEach(function (h3, index) {
      var questionText = (h3.textContent || '').trim();

      // Collect answer content: everything between this H3 and the next H3 (or end of container)
      var answerElements = [];
      var nextSib = h3.nextSibling;
      while (nextSib) {
        if (nextSib.nodeType === 1 && nextSib.tagName === 'H3') break;
        answerElements.push(nextSib);
        nextSib = nextSib.nextSibling;
      }

      // Build accordion item
      var item = document.createElement('div');
      item.className = 'sd-faq-item';
      item.setAttribute('data-faq-item', '');

      // Question button
      var questionBtn = document.createElement('button');
      questionBtn.className = 'sd-faq-item__question';
      questionBtn.setAttribute('type', 'button');
      questionBtn.setAttribute('aria-expanded', 'false');
      questionBtn.setAttribute('aria-controls', 'faq-answer-' + index);
      questionBtn.innerHTML =
        '<span>' + escapeHtml(questionText) + '</span>' +
        '<span class="sd-faq-item__icon">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</span>';

      // Answer panel
      var answerPanel = document.createElement('div');
      answerPanel.className = 'sd-faq-item__answer';
      answerPanel.id = 'faq-answer-' + index;
      answerPanel.setAttribute('role', 'region');
      answerPanel.setAttribute('aria-labelledby', 'faq-q-' + index);
      questionBtn.id = 'faq-q-' + index;

      var answerInner = document.createElement('div');
      answerInner.className = 'sd-faq-item__answer-inner';

      // Collect answer text for schema
      var answerText = '';
      answerElements.forEach(function (el) {
        answerInner.appendChild(el);
        answerText += (el.textContent || '') + ' ';
      });

      answerPanel.appendChild(answerInner);

      // Replace H3 with accordion item
      item.appendChild(questionBtn);
      item.appendChild(answerPanel);
      h3.parentNode.insertBefore(item, h3);
      h3.parentNode.removeChild(h3);

      // Store FAQ data for schema
      faqData.push({
        question: questionText,
        answer: answerText.trim()
      });

      // Click handler
      questionBtn.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');

        // Optional: close others (uncomment for single-open behavior)
        // document.querySelectorAll('.sd-faq-item.is-open').forEach(function(openItem) {
        //   openItem.classList.remove('is-open');
        //   openItem.querySelector('.sd-faq-item__question').setAttribute('aria-expanded', 'false');
        // });

        item.classList.toggle('is-open');
        questionBtn.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* -----------------------------------------------------------------------
     6. FAQ SCHEMA (JSON-LD) INJECTION - DISABLED
     ----------------------------------------------------------------------- 
     DISABLED: This section was generating invalid FAQPage schemas
     without proper mainEntity field, causing Google Search Console errors.
     If you want to re-enable FAQPage schema, ensure faqData is properly
     populated with question/answer pairs before generating schema.
     ----------------------------------------------------------------------- */
  /*
if (faqData.length > 0) {
    var faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqData.map(function (faq) {
        return {
          '@type': 'Question',
          'name': faq.question,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': faq.answer
          }
        };
      })
    };

    var scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(scriptEl);
  }
  */

  /* -----------------------------------------------------------------------
     7. TL;DR SECTION AUTO-DETECTION
     ----------------------------------------------------------------------- */
  // Look for an H2 with "TL;DR" or "TLDR" in its text
  headings.forEach(function (h) {
    var txt = (h.textContent || '').trim();
    if (/^tl;?dr/i.test(txt)) {
      // Gather content between this H2 and the next H2
      var tldrBox = document.createElement('div');
      tldrBox.className = 'sd-tldr-box';

      var contentElements = [];
      var next = h.nextElementSibling;
      while (next && next.tagName !== 'H2') {
        contentElements.push(next);
        next = next.nextElementSibling;
      }

      // Insert box after the H2
      h.parentNode.insertBefore(tldrBox, h.nextSibling);

      // Move content into box
      contentElements.forEach(function (el) {
        tldrBox.appendChild(el);
      });

      // Hide the TL;DR H2 (the box has its own label via CSS ::before)
      h.style.display = 'none';
    }
  });

  /* -----------------------------------------------------------------------
     8. TABLE WRAPPING FOR MOBILE
     ----------------------------------------------------------------------- */
  var tables = articleContent.querySelectorAll('table');
  tables.forEach(function (table) {
    // Skip if already wrapped
    if (table.parentNode.classList && table.parentNode.classList.contains('sd-table-wrapper')) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'sd-table-wrapper';
    wrapper.setAttribute('role', 'region');
    wrapper.setAttribute('aria-label', 'Scrollable table');
    wrapper.setAttribute('tabindex', '0');

    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);

    // Add scroll hint on mobile
    var hint = document.createElement('div');
    hint.className = 'sd-table-scroll-hint';
    hint.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
      '<span>Scroll to see more</span>';
    wrapper.parentNode.insertBefore(hint, wrapper.nextSibling);

    // Check if table is scrollable
    function checkScrollable() {
      if (table.scrollWidth > wrapper.clientWidth) {
        wrapper.classList.add('is-scrollable');
        hint.style.display = 'flex';
      } else {
        wrapper.classList.remove('is-scrollable');
        hint.style.display = 'none';
      }
    }

    checkScrollable();
    window.addEventListener('resize', debounce(checkScrollable, 200));

    // Hide hint once user scrolls
    wrapper.addEventListener('scroll', function () {
      hint.style.display = 'none';
    }, { once: true });
  });

  /* -----------------------------------------------------------------------
     9. SOURCES SECTION STYLING
     ----------------------------------------------------------------------- */
  headings.forEach(function (h) {
    var txt = (h.textContent || '').toLowerCase();
    if (txt.indexOf('sources') !== -1 || txt.indexOf('references') !== -1) {
      // Wrap everything between this H2 and the next H2 in a styled container
      var sourcesBox = document.createElement('div');
      sourcesBox.className = 'sd-sources';

      var contentEls = [];
      var next = h.nextElementSibling;
      while (next && next.tagName !== 'H2') {
        contentEls.push(next);
        next = next.nextElementSibling;
      }

      h.parentNode.insertBefore(sourcesBox, h);
      sourcesBox.appendChild(h);
      contentEls.forEach(function (el) {
        sourcesBox.appendChild(el);
      });
    }
  });

  /* -----------------------------------------------------------------------
     10. READING PROGRESS BAR
     ----------------------------------------------------------------------- */
  if (progressBar && progressFill) {
    var updateProgress = function () {
      var articleRect = articleContent.getBoundingClientRect();
      var articleTop = articleRect.top + window.scrollY;
      var articleHeight = articleRect.height;
      var windowHeight = window.innerHeight;
      var scrollY = window.scrollY;

      var progress = (scrollY - articleTop) / (articleHeight - windowHeight);
      progress = Math.max(0, Math.min(1, progress));

      progressFill.style.width = (progress * 100) + '%';
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }

  /* -----------------------------------------------------------------------
     11. BACK TO TOP BUTTON
     ----------------------------------------------------------------------- */
  if (backToTopBtn) {
    var toggleBackToTop = function () {
      if (window.scrollY > 500) {
        backToTopBtn.classList.add('is-visible');
      } else {
        backToTopBtn.classList.remove('is-visible');
      }
    };

    window.addEventListener('scroll', debounce(toggleBackToTop, 100), { passive: true });

    backToTopBtn.addEventListener('click', function () {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  /* -----------------------------------------------------------------------
     12. SMOOTH SCROLL FOR ANCHOR LINKS
     ----------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;

    var targetId = anchor.getAttribute('href');
    if (targetId === '#') return;

    var target;
    try {
      target = document.querySelector(targetId);
    } catch (err) {
      return;
    }

    if (target) {
      e.preventDefault();
      var offset = 80; // Account for sticky header
      var top = target.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({
        top: top,
        behavior: 'smooth'
      });

      // Update URL hash without jumping
      if (history.pushState) {
        history.pushState(null, null, targetId);
      }
    }
  });

  /* -----------------------------------------------------------------------
     HELPERS
     ----------------------------------------------------------------------- */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

})();
